import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CloseIcon } from '../../assets/svg/Breakdown/close.svg';
import { ReactComponent as ReplaceIcon } from '../../assets/svg/Breakdown/replace.svg';
import { SceneWithUserNames, SceneInteriorExterior } from '../../types/scene.types';
import { MultiColumnData, ElementFindReplaceData } from './useMultiColumnFindReplace';
import { compareSceneCodes } from '../../pages/Breakdown/BreakdownMain/Utils/SceneCodeUtils';
import './FindReplace.css';

interface FindReplaceResult {
  sceneIndex: number;
  sceneCode: string;
  fieldName: string;
  fieldKey?: string;
  originalContent: string;
  replacedContent: string;
  matchedText: string; // 匹配到的文本片段
  matchedIndex: number; // 匹配在原始内容中的起始位置
}

// onUpdate 回调：根据 columnIndex 传入相应类型的数据（联合类型便于传入具体 handler）
type OnUpdate =
  | ((updatedData: string[]) => void | Promise<void>)
  | ((updatedData: MultiColumnData[]) => void | Promise<void>)
  | ((updatedData: ElementFindReplaceData[]) => void | Promise<void>);

interface FindReplaceProps {
  visible: boolean;
  onClose: () => void;
  columnData: string[] | MultiColumnData[] | ElementFindReplaceData[]; // 当前列的所有数据
  columnIndex: string; // 当前列的标识符：'multi-column' | 'elements' | 单列key
  scenes?: SceneWithUserNames[]; // 场次数组，用于获取场号
  rowIdentifiers?: string[]; // 行标识符（用于 elements 模式显示元素名称）
  onUpdate: OnUpdate; // 更新数据的回调函数（支持异步）
  showRecommendMenu?: boolean; // 是否显示推荐菜单，默认为 true
}

const FindReplace: React.FC<FindReplaceProps> = ({
  visible,
  onClose,
  columnData,
  columnIndex,
  scenes = [],
  rowIdentifiers,
  onUpdate,
  showRecommendMenu = true
}) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [findReplaceResults, setFindReplaceResults] = useState<FindReplaceResult[]>([]);
  const [hasSearched, setHasSearched] = useState(false); // 标记是否已执行过搜索
  const [isReplacing, setIsReplacing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set()); // 选中的结果索引
  const [matchMode, setMatchMode] = useState<'keyword' | 'exact'>('keyword'); // 匹配模式：关键词/完全匹配
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const replaceButtonRef = useRef<HTMLButtonElement>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);
  const handleSearchRef = useRef<() => void>(() => {});
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;

  // 推荐菜单状态
  const [recommendVisible, setRecommendVisible] = useState(false);
  const [recommendResults, setRecommendResults] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const resultsRef = useRef<HTMLDivElement[]>([]);
  const prevVisibleRef = useRef(false);
  const prevColumnIndexRef = useRef(columnIndex);

  const normalizeSearchInput = (value: string): string => value.replace(/\t/g, '');

  // 更新推荐结果（仅用于单列模式）
  useEffect(() => {
    // 多列模式和场次元素模式不使用推荐菜单
    if (columnIndex === 'multi-column' || columnIndex === 'elements') {
      setRecommendResults([]);
      return;
    }
    
    // 单列模式：去重并过滤空值
    const stringData = columnData as string[];
    const uniqueData = Array.from(new Set(stringData.filter(item => item && typeof item === 'string' && item.trim() !== '')));
    
    // 如果输入框为空，显示所有去重后的数据
    if (!searchText.trim()) {
      // 按字母顺序排序
      const sorted = uniqueData.sort((a, b) => a.localeCompare(b, 'zh-CN'));
      setRecommendResults(sorted);
      return;
    }
    
    // 按关键词过滤
    const filtered = uniqueData.filter(item => 
      item.toLowerCase().includes(searchText.toLowerCase())
    );
    
    // 排除与当前输入完全匹配的结果
    const results = filtered.filter(item => 
      item.toLowerCase() !== searchText.toLowerCase()
    );
    
    // 按字母顺序排序
    results.sort((a, b) => a.localeCompare(b, 'zh-CN'));
    
    setRecommendResults(results);
  }, [searchText, columnData, columnIndex]);

  // 更新推荐菜单位置
  useEffect(() => {
    if (!recommendVisible || !searchInputRef.current || !containerRef.current) return;
    
    const inputRect = searchInputRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    
    const position = {
      top: inputRect.bottom - containerRect.top + 2,
      left: inputRect.left - containerRect.left,
      width: inputRect.width,
    };
    
    setMenuPosition(position);
  }, [recommendVisible]);

  // 多列模式下的字段名称列表（去掉剧情日）
  const multiColumnFields = useMemo(() => [
    { key: 'sceneSet', name: t('breakdown.set') },
    { key: 'scenePlot', name: t('breakdown.plot') },
    { key: 'sceneLocation', name: t('breakdown.shootingLocation') },
    { key: 'scenePeriod', name: t('breakdown.shootingPeriodShort') },
    { key: 'scenePlotSummary', name: t('breakdown.plotSummary') },
  ], [t]);

  // 场次元素模式下的字段名称列表
  const elementFields = useMemo(() => [
    { key: 'elementName', name: t('scene.elementName') },
    { key: 'elementAbbr', name: t('elementManager.shortName') },
  ], [t]);

  // 字段排序优先级：场景、拍摄日、剧情、地点、日历摘要
  const fieldPriority = useMemo(() => ({
    sceneSet: 0,
    scenePlotDay: 1,
    scenePlot: 2,
    sceneLocation: 3,
    scenePeriod: 4,
    scenePlotSummary: 5,
    elementName: 6,
    elementAbbr: 7,
  } as Record<string, number>), []);

  const sortFindReplaceResults = useMemo(() => {
    const getFieldPriority = (result: FindReplaceResult): number => {
      if (result.fieldKey && fieldPriority[result.fieldKey] !== undefined) {
        return fieldPriority[result.fieldKey];
      }
      return Number.MAX_SAFE_INTEGER;
    };

    const compareBySceneCode = (a: string, b: string): number => {
      return compareSceneCodes({ sceneCode: a } as SceneWithUserNames, { sceneCode: b } as SceneWithUserNames);
    };

    return (results: FindReplaceResult[]): FindReplaceResult[] => {
      return results
        .map((result, originalOrder) => ({ result, originalOrder }))
        .sort((a, b) => {
          const sceneCodeCompare = compareBySceneCode(a.result.sceneCode || '', b.result.sceneCode || '');
          if (sceneCodeCompare !== 0) return sceneCodeCompare;

          const fieldCompare = getFieldPriority(a.result) - getFieldPriority(b.result);
          if (fieldCompare !== 0) return fieldCompare;

          if (a.result.sceneIndex !== b.result.sceneIndex) {
            return a.result.sceneIndex - b.result.sceneIndex;
          }

          if (a.result.matchedIndex !== b.result.matchedIndex) {
            return a.result.matchedIndex - b.result.matchedIndex;
          }

          return a.originalOrder - b.originalOrder;
        })
        .map(item => item.result);
    };
  }, [fieldPriority]);

  // 获取字段名称
  const getFieldName = (columnKey: string): string => {
    const fieldMap: Record<string, string> = {
      'sceneSet': t('breakdown.set'),
      'scenePlotDay': t('findReplace.scriptDay'),
      'scenePlot': t('breakdown.plot'),
      'sceneLocation': t('breakdown.shootingLocation'),
      'scenePeriod': t('breakdown.shootingPeriodShort'),
      'scenePlotSummary': t('breakdown.plotSummary'),
    };
    return fieldMap[columnKey] || columnKey;
  };

  // 格式化内外字段的值
  const formatInteriorExterior = (value: SceneInteriorExterior | null | undefined): string => {
    if (!value) return '--';
    if (value === SceneInteriorExterior.INTERIOR) {
      return t('export.interiorShort');
    }
    if (value === SceneInteriorExterior.EXTERIOR) {
      return t('export.exteriorShort');
    }
    if (value === SceneInteriorExterior.INTERIOR_EXTERIOR) {
      return t('export.interiorExteriorShort');
    }
    return '--';
  };

  /** 多字段标题：A, B, C and D（中文：A、B、C和D） */
  const joinFieldNamesForScope = (names: string[]): string => {
    const list = names.filter(Boolean);
    if (list.length === 0) return '';
    if (list.length === 1) return list[0];
    const comma = t('findReplace.fieldSeparator');
    const andSep = t('findReplace.fieldListAnd');
    if (list.length === 2) return `${list[0]}${andSep}${list[1]}`;
    return `${list.slice(0, -1).join(comma)}${andSep}${list[list.length - 1]}`;
  };

  // 获取所有字段名称（用于多列查找替换）
  const getAllFieldNames = (): string => {
    if (columnIndex === 'multi-column') {
      return joinFieldNamesForScope(multiColumnFields.map(f => f.name));
    }
    if (columnIndex === 'elements') {
      return joinFieldNamesForScope(elementFields.map(f => f.name));
    }
    return getFieldName(columnIndex);
  };

  // 获取行标识符（场号或元素名称）
  const getRowIdentifier = (index: number): string => {
    if (rowIdentifiers && rowIdentifiers[index] !== undefined) {
      return rowIdentifiers[index];
    }
    return scenes[index]?.sceneCode || '';
  };

  // 行标识符列的表头文本
  const rowIdentifierHeader = columnIndex === 'elements' ? t('findReplace.elementName') : t('findReplace.sceneCode');

  // 渲染查找/替换内容（VSCode 风格）
  const renderFindReplaceContent = (result: FindReplaceResult): React.ReactNode => {
    const content = result.originalContent;
    const escapedKeyword = searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    
    if (!replaceText.trim()) {
      // 只显示查找内容：匹配的部分用浅红色高亮
      const regex = new RegExp(`(${escapedKeyword})`, 'gi');
      const parts = content.split(regex);
      
      return parts.map((part, index) => {
        // 检查是否匹配关键词（不区分大小写）
        if (regex.test(part)) {
          regex.lastIndex = 0;
          return (
            <span key={index} className="find-replace-highlight-match">
              {part}
            </span>
          );
        }
        return <React.Fragment key={index}>{part}</React.Fragment>;
      });
    }
    
    // 显示替换内容：原匹配部分用浅红色+删除线，新内容用浅绿色
    const regex = new RegExp(`(${escapedKeyword})`, 'gi');
    const parts = content.split(regex);
    
    return parts.map((part, index) => {
      if (regex.test(part)) {
        regex.lastIndex = 0;
        return (
          <React.Fragment key={index}>
            <span className="find-replace-highlight-remove">{part}</span>
            <span className="find-replace-highlight-add">{replaceText}</span>
          </React.Fragment>
        );
      }
      return <React.Fragment key={index}>{part}</React.Fragment>;
    });
  };

  // 搜索逻辑（内部使用 searchTextRef.current 保证防抖回调始终用最新关键词，避免闭包拿到旧值）
  const handleSearch = () => {
    const keyword = searchTextRef.current.trim();
    if (!keyword) {
      setFindReplaceResults([]);
      setHasSearched(false);
      return;
    }

    const results: string[] = [];
    const detailedResults: FindReplaceResult[] = [];
    const searchLower = keyword.toLowerCase();
    const isMultiColumn = columnIndex === 'multi-column';
    const isElements = columnIndex === 'elements';
    
    // 用于存储已处理的场次/元素索引（避免重复添加）
    const processedSceneIndices = new Set<number>();

    columnData.forEach((item, index) => {
      if (!item) return;
      
      if (isElements) {
        // 场次元素模式：搜索 名称、简称
        const elementData = item as ElementFindReplaceData;
        const fields = [
          { key: 'elementName', value: elementData.elementName },
          { key: 'elementAbbr', value: elementData.elementAbbr }
        ];
        const rowIdentifier = getRowIdentifier(index);
        
        fields.forEach((field, fieldIndex) => {
          const part = field.value || '';
          if (!part) return;
          
          let isMatch = false;
          let replacedContent = part;
          
          if (matchMode === 'exact') {
            if (part.trim().toLowerCase() === searchLower.trim()) {
              isMatch = true;
              replacedContent = replaceText;
            }
          } else {
            if (part.toLowerCase().includes(searchLower)) {
              isMatch = true;
              const regex = new RegExp(keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
              replacedContent = part.replace(regex, replaceText);
            }
          }
          
          if (isMatch) {
            const updatedData: ElementFindReplaceData = { ...elementData };
            (updatedData as any)[field.key] = replacedContent;
            
            let matchedText = part;
            let matchedIndex = 0;
            if (matchMode === 'keyword') {
              const regex = new RegExp(keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
              const match = regex.exec(part);
              if (match) matchedIndex = match.index;
            }
            
            if (!processedSceneIndices.has(index)) {
              results.push(JSON.stringify(elementData));
              processedSceneIndices.add(index);
            }
            
            detailedResults.push({
              sceneIndex: index,
              sceneCode: rowIdentifier,
              fieldName: elementFields[fieldIndex]?.name || '',
              fieldKey: field.key,
              originalContent: part,
              replacedContent: JSON.stringify(updatedData),
              matchedText: matchedText,
              matchedIndex: matchedIndex
            });
          }
        });
      } else if (isMultiColumn) {
        // 多列模式：使用结构化的 MultiColumnData
        const multiColumnItem = item as MultiColumnData;
        const rowIdentifier = getRowIdentifier(index);
        const fields = [
          { key: 'sceneSet', value: multiColumnItem.sceneSet },
          { key: 'scenePlot', value: multiColumnItem.scenePlot },
          { key: 'sceneLocation', value: multiColumnItem.sceneLocation },
          { key: 'scenePeriod', value: multiColumnItem.scenePeriod },
          { key: 'scenePlotSummary', value: multiColumnItem.scenePlotSummary }
        ];
        
        fields.forEach((field, fieldIndex) => {
          const part = field.value || '';
          if (!part) return;
          
          let isMatch = false;
          let replacedContent = part;
          
          if (matchMode === 'exact') {
            // 完全匹配模式：字段的整个值和查找内容完全匹配
            if (part.trim().toLowerCase() === searchLower.trim()) {
              isMatch = true;
              replacedContent = replaceText;
            }
          } else {
            // 关键词模式：所有符合条件的内容部分
            if (part.toLowerCase().includes(searchLower)) {
              isMatch = true;
              // 计算替换后的内容
              const regex = new RegExp(keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
              replacedContent = part.replace(regex, replaceText);
            }
          }
          
          if (isMatch) {
            // 构建完整的替换内容（更新对应字段）
            const updatedMultiColumnData: MultiColumnData = { ...multiColumnItem };
            updatedMultiColumnData[field.key as keyof MultiColumnData] = replacedContent;
            
            // 提取匹配的文本片段（用于显示）
            let matchedText = part;
            let matchedIndex = 0;
            
            if (matchMode === 'keyword') {
              const regex = new RegExp(keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
              const match = regex.exec(part);
              if (match) {
                matchedIndex = match.index;
                matchedText = part;
              }
            } else {
              matchedText = part;
              matchedIndex = 0;
            }
            
            if (!processedSceneIndices.has(index)) {
              results.push(JSON.stringify(multiColumnItem));
              processedSceneIndices.add(index);
            }
            
            detailedResults.push({
              sceneIndex: index,
              sceneCode: rowIdentifier,
              fieldName: multiColumnFields[fieldIndex]?.name || '',
              fieldKey: field.key,
              originalContent: part,
              replacedContent: JSON.stringify(updatedMultiColumnData), // 存储结构化的替换内容
              matchedText: matchedText,
              matchedIndex: matchedIndex
            });
          }
        });
      } else {
        // 单列模式：原有逻辑（明确类型断言为 string）
        const stringItem = item as string;
        const fieldName = getFieldName(columnIndex);
        let isMatch = false;
        let replacedContent = stringItem;
        
        if (matchMode === 'exact') {
          // 完全匹配模式：字段的整个值和查找内容完全匹配
          if (stringItem.trim().toLowerCase() === searchLower.trim()) {
            isMatch = true;
            replacedContent = replaceText;
          }
        } else {
          // 关键词模式：所有符合条件的内容部分
          if (stringItem.toLowerCase().includes(searchLower)) {
            isMatch = true;
            // 计算替换后的内容
            const regex = new RegExp(keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
            replacedContent = stringItem.replace(regex, replaceText);
          }
        }
        
        if (isMatch) {
          results.push(stringItem);
          
          // 获取场号
          const sceneCode = scenes[index]?.sceneCode || '';
          
          // 提取匹配的文本片段（用于显示）
          let matchedText = stringItem;
          let matchedIndex = 0;
          
          if (matchMode === 'keyword') {
            // 关键词模式：找到第一个匹配的位置
            const regex = new RegExp(keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
            const match = regex.exec(stringItem);
            if (match) {
              matchedIndex = match.index;
              matchedText = stringItem; // 显示整个字段值，但高亮匹配部分
            }
          } else {
            // 完全匹配模式：整个字段值就是匹配的文本
            matchedText = stringItem;
            matchedIndex = 0;
          }
          
          detailedResults.push({
            sceneIndex: index,
            sceneCode: sceneCode,
            fieldName: fieldName,
            fieldKey: columnIndex,
            originalContent: stringItem,
            replacedContent: replacedContent,
            matchedText: matchedText,
            matchedIndex: matchedIndex
          });
        }
      }
    });

    const sortedResults = sortFindReplaceResults(detailedResults);
    setFindReplaceResults(sortedResults);
    setHasSearched(true); // 标记已执行过搜索
    setRecommendVisible(false); // 执行搜索后关闭推荐菜单
    // 搜索后默认全选所有结果
    setSelectedIndices(new Set(sortedResults.map((_, index) => index)));
  };

  handleSearchRef.current = handleSearch;

  // 唯一搜索触发：监听 input 变更，防抖后执行搜索（不依赖 React 状态，避免 IME/composition 导致遗漏）
  useEffect(() => {
    const input = searchInputRef.current;
    if (!input || !visible) return;
    let timer: ReturnType<typeof setTimeout>;
    const handleInput = () => {
      const value = normalizeSearchInput(input.value);
      if (value !== input.value) {
        input.value = value;
      }
      searchTextRef.current = value;
      setSearchText(value);
      clearTimeout(timer);
      if (value.trim()) {
        setFindReplaceResults([]);
        setHasSearched(false);
        setSelectedIndices(new Set());
        timer = setTimeout(() => handleSearchRef.current(), 150);
      } else {
        setFindReplaceResults([]);
        setHasSearched(false);
        setSelectedIndices(new Set());
      }
    };
    input.addEventListener('input', handleInput);
    return () => {
      clearTimeout(timer);
      input.removeEventListener('input', handleInput);
    };
  }, [visible, matchMode]);

  // 当替换文本变化时，更新结果列表中的替换内容（使用 functional update 避免 findReplaceResults 依赖导致循环）
  useEffect(() => {
    if (!searchText.trim()) return;

    const isMultiColumn = columnIndex === 'multi-column';
    const isElements = columnIndex === 'elements';

    setFindReplaceResults(prev => {
      if (prev.length === 0) return prev;
      return prev.map(result => {
        let replacedContent = result.originalContent;
        let matchedText = result.matchedText || result.originalContent;
        let matchedIndex = result.matchedIndex || 0;
        
        if (isElements) {
          try {
            const originalData = columnData[result.sceneIndex] as ElementFindReplaceData;
            const fieldIndex = result.fieldKey
              ? elementFields.findIndex(f => f.key === result.fieldKey)
              : elementFields.findIndex(f => f.name === result.fieldName);
            
            if (fieldIndex >= 0 && originalData) {
              const fieldKey = elementFields[fieldIndex].key as keyof ElementFindReplaceData;
              const part = originalData[fieldKey] || '';
              let updatedPart = part;
              
              if (matchMode === 'exact') {
                if (part.trim().toLowerCase() === searchText.trim().toLowerCase()) {
                  updatedPart = replaceText;
                }
              } else {
                const regex = new RegExp(searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
                updatedPart = part.replace(regex, replaceText);
              }
              
              const updatedData: ElementFindReplaceData = { ...originalData };
              updatedData[fieldKey] = updatedPart;
              replacedContent = JSON.stringify(updatedData);
              matchedText = part;
            }
          } catch {
            // 解析失败时保留原样
          }
        } else if (isMultiColumn) {
          try {
            const originalData = columnData[result.sceneIndex] as MultiColumnData;
            const fieldIndex = result.fieldKey
              ? multiColumnFields.findIndex(f => f.key === result.fieldKey)
              : multiColumnFields.findIndex(f => f.name === result.fieldName);
            
            if (fieldIndex >= 0 && originalData) {
              const fieldKey = multiColumnFields[fieldIndex].key as keyof MultiColumnData;
              const part = originalData[fieldKey] || '';
              let updatedPart = part;
              
              if (matchMode === 'exact') {
                if (part.trim().toLowerCase() === searchText.trim().toLowerCase()) {
                  updatedPart = replaceText;
                }
              } else {
                const regex = new RegExp(searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
                updatedPart = part.replace(regex, replaceText);
              }
              
              const updatedData: MultiColumnData = { ...originalData };
              updatedData[fieldKey] = updatedPart;
              replacedContent = JSON.stringify(updatedData);
              
              // 更新 matchedText
              matchedText = part;
            }
          } catch (e) {
            // 如果解析失败，使用原始逻辑（向后兼容）
            const parts = result.originalContent.split(' | ');
            const fieldIndex = multiColumnFields.findIndex(f => f.name === result.fieldName);
            
            if (fieldIndex >= 0 && fieldIndex < parts.length) {
              const part = parts[fieldIndex];
              let updatedPart = part;
              
              if (matchMode === 'exact') {
                if (part.trim().toLowerCase() === searchText.trim().toLowerCase()) {
                  updatedPart = replaceText;
                }
              } else {
                const regex = new RegExp(searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
                updatedPart = part.replace(regex, replaceText);
              }
              
              const updatedParts = [...parts];
              updatedParts[fieldIndex] = updatedPart;
              replacedContent = updatedParts.join(' | ');
              
              matchedText = part;
            }
          }
        } else {
          // 单列模式
          if (matchMode === 'exact') {
            // 完全匹配模式：直接替换为替换文本
            if (result.originalContent.trim().toLowerCase() === searchText.trim().toLowerCase()) {
              replacedContent = replaceText;
            }
          } else {
            // 关键词模式：替换匹配的部分
            const regex = new RegExp(searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
            replacedContent = result.originalContent.replace(regex, replaceText);
          }
          
          // 更新 matchedText
          matchedText = result.originalContent;
        }
        
        return {
          ...result,
          replacedContent: replacedContent,
          matchedText: matchedText,
          matchedIndex: matchedIndex
        };
      });
    });
  }, [replaceText, searchText, matchMode, columnIndex, columnData, multiColumnFields, elementFields]);

  // 全选/取消全选
  const handleSelectAll = () => {
    if (selectedIndices.size === findReplaceResults.length) {
      // 取消全选
      setSelectedIndices(new Set());
    } else {
      // 全选
      setSelectedIndices(new Set(findReplaceResults.map((_, index) => index)));
    }
  };

  // 切换单个复选框
  const handleToggleSelect = (index: number) => {
    setSelectedIndices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // 替换逻辑
  const handleReplace = async () => {
    if (selectedIndices.size === 0) {
      setError(t('findReplace.selectAtLeastOne'));
      return;
    }

    setIsReplacing(true);
    setError(null);

    try {
      const isMultiColumn = columnIndex === 'multi-column';
      const isElements = columnIndex === 'elements';
      
      // 只替换选中的项
      const updatedData: (string | MultiColumnData | ElementFindReplaceData)[] = columnData.map((item, index) => {
        const matchingResults = findReplaceResults.filter(
          (result, resultIndex) => result.sceneIndex === index && selectedIndices.has(resultIndex)
        );
        
        if (matchingResults.length === 0) {
          return item;
        }
        
        if (!item) return item;
        
        if (isElements) {
          const elementData = item as ElementFindReplaceData;
          const updated: ElementFindReplaceData = { ...elementData };
          
          matchingResults.forEach(result => {
            const fieldIndex = elementFields.findIndex(f => f.name === result.fieldName);
            const safeFieldIndex = result.fieldKey
              ? elementFields.findIndex(f => f.key === result.fieldKey)
              : fieldIndex;
            if (safeFieldIndex >= 0) {
              const fieldKey = elementFields[safeFieldIndex].key as keyof ElementFindReplaceData;
              let replacedPart = result.originalContent;
              if (matchMode === 'exact') {
                if (result.originalContent.trim().toLowerCase() === searchText.trim().toLowerCase()) {
                  replacedPart = replaceText;
                }
              } else {
                const regex = new RegExp(searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
                replacedPart = result.originalContent.replace(regex, replaceText);
              }
              updated[fieldKey] = replacedPart;
            }
          });
          return updated;
        }
        
        if (isMultiColumn) {
          const multiColumnItem = item as MultiColumnData;
          const updatedData: MultiColumnData = { ...multiColumnItem };
          
          matchingResults.forEach(result => {
            const fieldIndex = multiColumnFields.findIndex(f => f.name === result.fieldName);
            const safeFieldIndex = result.fieldKey
              ? multiColumnFields.findIndex(f => f.key === result.fieldKey)
              : fieldIndex;
            if (safeFieldIndex >= 0) {
              const fieldKey = multiColumnFields[safeFieldIndex].key as keyof MultiColumnData;
              // 直接对 originalContent 进行替换
              let replacedPart = result.originalContent;
              
              if (matchMode === 'exact') {
                // 完全匹配模式：字段的整个值和查找内容完全匹配时才替换
                if (result.originalContent.trim().toLowerCase() === searchText.trim().toLowerCase()) {
                  replacedPart = replaceText;
                }
              } else {
                // 关键词模式：使用正则表达式进行全局替换（不区分大小写）
                const regex = new RegExp(searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
                replacedPart = result.originalContent.replace(regex, replaceText);
              }
              
              updatedData[fieldKey] = replacedPart;
            }
          });
          
          return updatedData;
        } else {
          // 单列模式
          const stringItem = item as string;
          if (matchMode === 'exact') {
            // 完全匹配模式：字段的整个值和查找内容完全匹配时才替换
            if (stringItem.trim().toLowerCase() === searchText.trim().toLowerCase()) {
              return replaceText;
            }
          } else {
            // 关键词模式：使用正则表达式进行全局替换（不区分大小写）
            const regex = new RegExp(searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
            return stringItem.replace(regex, replaceText);
          }
        }
        
        return item;
      });

      // 根据模式转换数据类型并调用 onUpdate
      if (isElements) {
        await (onUpdate as (updatedData: ElementFindReplaceData[]) => void | Promise<void>)(updatedData as ElementFindReplaceData[]);
      } else if (isMultiColumn) {
        await (onUpdate as (updatedData: MultiColumnData[]) => void | Promise<void>)(updatedData as MultiColumnData[]);
      } else {
        await (onUpdate as (updatedData: string[]) => void | Promise<void>)(updatedData as string[]);
      }
      onClose();
    } catch (err) {
      console.error('替换失败:', err);
      setError(err instanceof Error ? err.message : '替换失败，请重试');
    } finally {
      setIsReplacing(false);
    }
  };

  // 处理选择推荐项
  const handleSelectRecommend = (text: string) => {
    searchTextRef.current = text;
    setSearchText(text);
    if (searchInputRef.current) {
      searchInputRef.current.value = text;
    }
    setRecommendVisible(false);
    setSelectedIndex(-1);
    setFindReplaceResults([]);
    setHasSearched(false);
    setSelectedIndices(new Set());
    setTimeout(() => {
      handleSearchRef.current();
      searchInputRef.current?.focus();
    }, 0);
  };

  // 监听方向键和回车键
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!visible) return;
      
      // 如果推荐菜单可见，处理方向键导航
      if (recommendVisible && recommendResults.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex(prev => {
            if (prev < 0) return 0;
            if (prev >= recommendResults.length - 1) return 0;
            return prev + 1;
          });
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex(prev => {
            if (prev <= 0) return recommendResults.length - 1;
            return prev - 1;
          });
        } else if (e.key === 'Enter') {
          if (selectedIndex >= 0 && selectedIndex < recommendResults.length) {
            e.preventDefault();
            handleSelectRecommend(recommendResults[selectedIndex]);
          }
        } else if (e.key === 'Escape') {
          setRecommendVisible(false);
          setSelectedIndex(-1);
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [visible, recommendVisible, recommendResults, selectedIndex]);

  // 滚动到选中项
  useEffect(() => {
    if (selectedIndex >= 0 && resultsRef.current[selectedIndex]) {
      resultsRef.current[selectedIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [selectedIndex]);

  // 点击外部区域关闭推荐菜单和查找替换组件
  useEffect(() => {
    if (!visible) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      // 检查是否点击了 FindReplace 容器内部
      if (containerRef.current?.contains(target)) {
        return;
      }
      
      // 检查是否点击了推荐菜单
      const recommendMenu = document.querySelector('.find-replace-recommend-menu');
      if (recommendMenu?.contains(target)) {
        return;
      }
      
      // 点击外部时关闭整个查找替换组件
      onClose();
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [visible, onClose]);

  // 焦点循环：仅在查找框、替换框、替换按钮之间使用 Tab 导航
  useEffect(() => {
    if (!visible) return;

    const focusables = [
      searchInputRef.current,
      replaceInputRef.current,
      replaceButtonRef.current,
    ].filter(Boolean) as HTMLElement[];

    if (focusables.length === 0) return;

    // 打开弹窗后默认聚焦查找输入框
    focusables[0].focus();

    const handleDialogKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const active = document.activeElement as HTMLElement | null;
      const currentIndex = focusables.findIndex((el) => el === active);

      // 当焦点不在目标元素内时，也重置到第一个，避免跳出组件
      if (currentIndex === -1) {
        e.preventDefault();
        focusables[0].focus();
        return;
      }

      e.preventDefault();
      const nextIndex = e.shiftKey
        ? (currentIndex - 1 + focusables.length) % focusables.length
        : (currentIndex + 1) % focusables.length;
      focusables[nextIndex].focus();
    };

    document.addEventListener('keydown', handleDialogKeyDown, true);
    return () => document.removeEventListener('keydown', handleDialogKeyDown, true);
  }, [visible]);

  // 仅在「打开弹窗」或「切换列」时重置状态。
  // columnData 由父组件每次 map 得到新数组引用，若加入依赖会在弹窗打开期间反复重置，导致搜索结果被清空（例如从候选选中后结果一闪消失）。
  useEffect(() => {
    const justOpened = visible && !prevVisibleRef.current;
    const columnChangedWhileOpen =
      visible && prevVisibleRef.current && columnIndex !== prevColumnIndexRef.current;

    if (visible && (justOpened || columnChangedWhileOpen)) {
      setError(null);
      setSelectedIndex(-1);
      setSelectedIndices(new Set());
      setSearchText('');
      searchTextRef.current = '';

      if (searchInputRef.current) {
        searchInputRef.current.value = '';
      }
      setFindReplaceResults([]);
      setHasSearched(false);

      const isRecommendColumn =
        showRecommendMenu && columnIndex !== 'multi-column' && columnIndex !== 'elements';

      if (columnIndex !== 'multi-column' && columnIndex !== 'elements') {
        const stringData = columnData as string[];
        const uniqueData = Array.from(
          new Set(stringData.filter(item => item && typeof item === 'string' && item.trim() !== ''))
        );
        const sorted = uniqueData.sort((a, b) => a.localeCompare(b, 'zh-CN'));
        setRecommendResults(sorted);
        setRecommendVisible(isRecommendColumn && sorted.length > 0);
      } else {
        setRecommendVisible(false);
      }
    } else if (!visible) {
      setRecommendVisible(false);
    }

    prevVisibleRef.current = visible;
    prevColumnIndexRef.current = columnIndex;
    // columnData 仅在上面的重置分支中读取；不加入依赖，避免父组件每次 render 传入新数组引用时反复清空搜索状态
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, columnIndex, showRecommendMenu]);

  if (!visible) return null;

  return (
    <div ref={containerRef} className="find-replace-container">
      <div className="find-replace-dialog">
        {/* 右上角关闭按钮 */}
        <button 
          className="find-replace-close-btn" 
          onClick={onClose}
          title={t('common.close')}
        >
          <CloseIcon className="find-replace-close-icon" aria-label={t('common.close')} />
        </button>

        {/* 标题 */}
        <div className="find-replace-title">
          <span className="find-replace-title-text">
            {t('findReplace.scopeTitle')}{getAllFieldNames()}
          </span>
          <div className="find-replace-mode-switch">
            <button
              className={`find-replace-mode-switch-btn ${matchMode === 'keyword' ? 'active' : ''}`}
              onClick={() => {
                if (matchMode !== 'keyword') {
                  setMatchMode('keyword');
                  // 切换模式后重新搜索
                  if (hasSearched && searchText.trim()) {
                    setTimeout(() => handleSearch(), 0);
                  }
                }
              }}
              title={t('findReplace.keywordMode')}
            >
              {t('findReplace.keyword')}
            </button>
            <button
              className={`find-replace-mode-switch-btn ${matchMode === 'exact' ? 'active' : ''}`}
              onClick={() => {
                if (matchMode !== 'exact') {
                  setMatchMode('exact');
                  // 切换模式后重新搜索
                  if (hasSearched && searchText.trim()) {
                    setTimeout(() => handleSearch(), 0);
                  }
                }
              }}
              title={t('findReplace.exactMatchMode')}
            >
              {t('findReplace.exactMatch')}
            </button>
          </div>
        </div>

        {/* 第一行：查找输入框 + 替换图标 + 替换输入框 + "替换"按钮 */}
        <div className="find-replace-input-row" style={{ position: 'relative' }}>
          <input
            ref={searchInputRef}
            type="text"
            className="find-replace-search-input"
            placeholder={t('findReplace.findPlaceholder')}
            defaultValue=""
            onChange={(e) => {
              const value = normalizeSearchInput(e.target.value);
              if (value !== e.target.value) {
                e.target.value = value;
              }
              setSearchText(value);
              searchTextRef.current = value;
              if (showRecommendMenu && !recommendVisible) {
                setRecommendVisible(true);
              }
            }}
            onFocus={() => {
              // 打开瞬间 focus 可能早于 recommendResults 的 state 更新，不为 length 设限；无候选时下方渲染条件仍会挡住空菜单
              if (
                showRecommendMenu &&
                columnIndex !== 'multi-column' &&
                columnIndex !== 'elements'
              ) {
                setRecommendVisible(true);
              }
            }}
            onBlur={() => {
              // 失焦时延迟关闭推荐菜单（给点击推荐项留出时间）
              setTimeout(() => {
                const activeElement = document.activeElement;
                const isClickingRecommend = activeElement?.closest('.find-replace-recommend-menu');
                if (!isClickingRecommend) {
                  setRecommendVisible(false);
                  setSelectedIndex(-1);
                }
              }, 200);
            }}
          />
          <ReplaceIcon className="find-replace-icon" />
          <input
            ref={replaceInputRef}
            type="text"
            className="find-replace-replace-input"
            placeholder={t('findReplace.replacePlaceholder')}
            value={replaceText}
            onChange={(e) => setReplaceText(e.target.value)}
          />
          <button 
            ref={replaceButtonRef}
            className="find-replace-replace-btn"
            onClick={handleReplace}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleReplace();
              }
            }}
            disabled={selectedIndices.size === 0 || isReplacing}
          >
            {isReplacing ? t('findReplace.replacing') : t('findReplace.replace')}
          </button>
        </div>

        {/* 结果统计和全选 */}
        {hasSearched && (
          <div className="find-replace-results-info">
            <span className="find-replace-results-count">
              {t('findReplace.foundCount', { count: findReplaceResults.length })}
            </span>
            {findReplaceResults.length > 0 && (
              <button 
                className="find-replace-select-all-btn"
                onClick={handleSelectAll}
              >
                {selectedIndices.size === findReplaceResults.length ? t('findReplace.deselectAll') : t('findReplace.selectAll')}
              </button>
            )}
          </div>
        )}

        {/* 错误提示 */}
        {error && (
          <div className="find-replace-error">
            {error}
          </div>
        )}

        {/* 查找结果列表 */}
        {hasSearched && findReplaceResults.length > 0 && (
          <div className="find-replace-results-list">
            {/* 单列模式（剧情日、拍摄地点）：显示场景和剧情列 */}
            {(columnIndex === 'scenePlotDay' || columnIndex === 'sceneLocation') ? (
              <>
                <div className="find-replace-results-header">
                  <div className="find-replace-results-col-header find-replace-checkbox-header" style={{ width: '30px' }}></div>
                  <div className="find-replace-results-col-header" style={{ width: '15%' }}>{t('findReplace.sceneCode')}</div>
                  {columnIndex === 'sceneLocation' && (
                    <div className="find-replace-results-col-header" style={{ width: '10%' }}>{t('scene.interiorExterior')}</div>
                  )}
                  <div className="find-replace-results-col-header" style={{ width: columnIndex === 'sceneLocation' ? '15%' : '18%' }}>{t('breakdown.set')}</div>
                  <div className="find-replace-results-col-header" style={{ width: columnIndex === 'sceneLocation' ? '18%' : '20%' }}>{t('breakdown.plot')}</div>
                  <div className="find-replace-results-col-header" style={{ width: columnIndex === 'sceneLocation' ? 'calc(42% - 30px)' : 'calc(47% - 30px)' }}>{t('findReplace.findReplaceContent')}</div>
                </div>
                <div className="find-replace-results-body" ref={resultsContainerRef}>
                  {findReplaceResults.map((result, index) => {
                    const scene = scenes[result.sceneIndex];
                    return (
                      <div key={index} className="find-replace-results-row">
                        <div className="find-replace-results-col find-replace-checkbox-col" style={{ width: '30px' }}>
                          <input
                            type="checkbox"
                            checked={selectedIndices.has(index)}
                            onChange={() => handleToggleSelect(index)}
                            className="find-replace-checkbox"
                          />
                        </div>
                        <div className="find-replace-results-col" style={{ width: '15%' }} title={result.sceneCode}>
                          {result.sceneCode || '--'}
                        </div>
                        {columnIndex === 'sceneLocation' && (
                          <div className="find-replace-results-col" style={{ width: '10%' }} title={formatInteriorExterior(scene?.sceneInteriorExterior)}>
                            {formatInteriorExterior(scene?.sceneInteriorExterior)}
                          </div>
                        )}
                        <div className="find-replace-results-col" style={{ width: columnIndex === 'sceneLocation' ? '15%' : '18%' }} title={scene?.sceneSet || ''}>
                          {scene?.sceneSet || '--'}
                        </div>
                        <div className="find-replace-results-col" style={{ width: columnIndex === 'sceneLocation' ? '18%' : '20%' }} title={scene?.scenePlot || ''}>
                          {scene?.scenePlot || '--'}
                        </div>
                        <div className="find-replace-results-col find-replace-content-col" style={{ width: columnIndex === 'sceneLocation' ? 'calc(42% - 30px)' : 'calc(47% - 30px)' }} title={result.originalContent}>
                          {renderFindReplaceContent(result)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              /* 多列模式 / 场次元素模式：显示字段列 */
              <>
                <div className="find-replace-results-header">
                  <div className="find-replace-results-col-header find-replace-checkbox-header" style={{ width: '30px' }}></div>
                  <div className="find-replace-results-col-header" style={{ width: 'calc(15% - 30px)' }}>{rowIdentifierHeader}</div>
                  <div className="find-replace-results-col-header" style={{ width: columnIndex === 'elements' ? '12%' : '15%' }}>{t('findReplace.field')}</div>
                  {columnIndex === 'elements' && (
                    <div className="find-replace-results-col-header" style={{ width: '18%' }}>{t('findReplace.category')}</div>
                  )}
                  <div className="find-replace-results-col-header" style={{ width: columnIndex === 'elements' ? 'calc(58% - 30px)' : 'calc(70% - 30px)' }}>{t('findReplace.findReplaceContent')}</div>
                </div>
                <div className="find-replace-results-body" ref={resultsContainerRef}>
                  {findReplaceResults.map((result, index) => {
                    const elementData = columnIndex === 'elements'
                      ? (columnData[result.sceneIndex] as ElementFindReplaceData | undefined)
                      : undefined;
                    const categoryName = elementData?.elementType
                      ? t(`elementType.${elementData.elementType}`)
                      : '--';

                    return (
                      <div key={index} className="find-replace-results-row">
                        <div className="find-replace-results-col find-replace-checkbox-col" style={{ width: '30px' }}>
                          <input
                            type="checkbox"
                            checked={selectedIndices.has(index)}
                            onChange={() => handleToggleSelect(index)}
                            className="find-replace-checkbox"
                          />
                        </div>
                        <div className="find-replace-results-col" style={{ width: 'calc(15% - 30px)' }} title={result.sceneCode}>
                          {result.sceneCode || '--'}
                        </div>
                        <div className="find-replace-results-col" style={{ width: columnIndex === 'elements' ? '12%' : '15%' }} title={result.fieldName}>
                          {result.fieldName}
                        </div>
                        {columnIndex === 'elements' && (
                          <div className="find-replace-results-col" style={{ width: '18%' }} title={categoryName}>
                            {categoryName}
                          </div>
                        )}
                        <div className="find-replace-results-col find-replace-content-col" style={{ width: columnIndex === 'elements' ? 'calc(58% - 30px)' : 'calc(70% - 30px)' }} title={result.originalContent}>
                          {renderFindReplaceContent(result)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>
      
      {/* 推荐菜单 - 使用 Portal 渲染到容器内 */}
      {showRecommendMenu && recommendVisible && recommendResults.length > 0 && menuPosition && containerRef.current && (
        createPortal(
          <div 
            className="find-replace-recommend-menu"
            style={{
              position: 'absolute',
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              width: `${menuPosition.width}px`,
              zIndex: 10001,
            }}
          >
            <div className="find-replace-recommend-dialog">
              <div className="find-replace-recommend-results">
                {recommendResults.map((text, index) => (
                  <div
                    key={text}
                    ref={(el) => {
                      if (el) {
                        resultsRef.current[index] = el;
                      }
                    }}
                    className={`find-replace-recommend-item ${
                      selectedIndex === index ? 'find-replace-recommend-item-selected' : ''
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault(); // 阻止输入框失焦
                      e.stopPropagation();
                      handleSelectRecommend(text);
                    }}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <span className="find-replace-recommend-name">{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>,
          containerRef.current
        )
      )}
    </div>
  );
};

export default FindReplace;